import { useState, useEffect } from 'react'
import { useAppData, useBillContext } from '../data'
import { ShareButton } from './ShareButton'
import {
  bcnLawUrl,
  billTrackingUrl,
  currentLifecycleStage,
  durationBetween,
  durationStyleDone,
  durationStylePending,
  formatDateEs,
  formatDateShort,
  legislatorDisplay,
} from '../helpers'
import { resolvePresident, photoUrl } from '../presidents'
import { scrollToElementSmooth } from './lifecycle/voteLinks'

interface BillInfoProps {
  showVoteContext?: boolean
}

export function BillInfo({ showVoteContext = false }: BillInfoProps) {
  const app = useAppData()
  const billCtx = useBillContext()

  const bill = billCtx.selectedBoletin != null
    ? app.cachedBill(billCtx.selectedBoletin)
    : undefined

  const president = bill != null ? resolvePresident(bill.filing_date) : null
  const [photoFailed, setPhotoFailed] = useState(false)

  useEffect(() => {
    setPhotoFailed(false)
  }, [president?.slug])

  if (bill == null) {
    return (
      <div className="bill-section bill-section--loading">
        <p>Cargando proyecto…</p>
      </div>
    )
  }

  const subjects = bill.subjects
  const authors = bill.authors

  const statusClass = bill.status.includes('Publicado')
    ? 'bill-status--published'
    : 'bill-status--pending'

  const [currentStage, currentStageAnchor] = currentLifecycleStage(bill)

  const statusText = (() => {
    if (bill.law_number != null && bill.law_number !== '') return bill.law_number
    if (bill.status === 'En tramitación') return currentStage
    return bill.status
  })()

  const lawUrl = (bill.law_number != null && bill.law_number !== '')
    ? bcnLawUrl(bill.law_number)
    : null
  const trackingUrl = billTrackingUrl(bill.boletin, bill.origin_chamber)
  const statusLinkUrl = lawUrl ?? trackingUrl
  const statusLinkLabel = lawUrl != null
    ? 'Ver texto oficial de la ley'
    : 'Ver tramitación oficial del proyecto'

  const isPublished = bill.status.includes('Publicado')
  const duration = (() => {
    if (isPublished) {
      const lastStage = bill.stages[bill.stages.length - 1]
      const lastDate = lastStage?.date
      if (lastDate != null && lastDate !== '') {
        return durationBetween(bill.filing_date, lastDate)
      }
      return null
    } else {
      const today = new Date()
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
      return durationBetween(bill.filing_date, todayStr)
    }
  })()

  const voteContextLabel = showVoteContext
    ? (() => {
        const vs = bill.vote_sessions[0]
        if (vs == null) return null
        const dateDisplay = formatDateShort(vs.date)
        return (
          <div className="vote-context-label">
            <span className="vote-context-icon">🗳</span>
            <span>{`Última votación en el Congreso — ${dateDisplay}`}</span>
          </div>
        )
      })()
    : null

  return (
    <>
      {voteContextLabel}
      <div className="bill-section">
        <div className="bill-details">
          <div className="bill-header-row">
            <span className="bill-badge">{bill.boletin}</span>
            {statusLinkUrl != null ? (
              <a
                href={statusLinkUrl}
                target="_blank"
                rel="noopener"
                title={statusLinkLabel}
                className={`bill-status ${statusClass}`}
              >
                {statusText}{' ↗'}
              </a>
            ) : (
              <span className={`bill-status ${statusClass}`}>{statusText}</span>
            )}
            {duration != null && (
              isPublished ? (() => {
                const [emoji, cls] = durationStyleDone(duration.days)
                return <span className={`bill-duration ${cls}`}>{`${emoji} ${duration.label}`}</span>
              })() : (() => {
                const [emoji, cls] = durationStylePending(duration.days)
                return <span className={`bill-duration ${cls}`}>{`${emoji} ${duration.label} en trámite`}</span>
              })()
            )}
            <ShareButton boletin={bill.boletin} />
          </div>
          <h2 className="bill-title">{bill.title}</h2>
          <div className="bill-meta-row">
            <span>{`📅 ${formatDateEs(bill.filing_date)}`}</span>
            <span>{`🏛 Origen: ${bill.origin_chamber}`}</span>
            {currentStageAnchor != null ? (
              <button
                type="button"
                className="bill-meta-link"
                onClick={() => scrollToElementSmooth(currentStageAnchor)}
              >
                {`🧭 Etapa actual: ${currentStage}`}
              </button>
            ) : (
              <span>{`🧭 Etapa actual: ${currentStage}`}</span>
            )}
          </div>
          {subjects.length > 0 && (
            <div className="bill-subjects">
              {subjects.map((s) => (
                <span key={s} className="subject-tag">{s}</span>
              ))}
            </div>
          )}
        </div>
        <div className="authors-panel">
          {authors.length > 0 ? (
            <>
              <div className="authors-header">
                <span className="authors-label">¿Quién presentó este proyecto?</span>
                <span className="author-role-tag">{authors.length > 1 ? 'Autores' : 'Autor'}</span>
              </div>
              {authors.map((author) => {
                const [, initials] = legislatorDisplay(
                  author.first_name,
                  author.last_name,
                  author.author_name,
                )
                const last = author.last_name != null
                  ? author.last_name
                  : (author.author_name.split(',')[0] ?? '').trim()
                const first = author.first_name ?? ''
                const fullName = first !== '' ? `${first} ${last}` : last
                const partyInfo = author.party != null
                  ? `${author.party} · ${author.chamber ?? ''}`
                  : author.author_name

                return (
                  <div key={author.author_name} className="author-item">
                    <div className="author-avatar">{initials}</div>
                    <div className="author-info">
                      <span className="author-name">{fullName}</span>
                      <span className="author-party">{partyInfo}</span>
                    </div>
                  </div>
                )
              })}
            </>
          ) : (
            <>
              <div className="authors-label">Origen del proyecto</div>
              {president != null && !photoFailed ? (
                <div className="author-item">
                  <div className="author-avatar presidente-avatar">
                    <img
                      src={photoUrl(president.slug)}
                      alt={president.name}
                      onError={() => setPhotoFailed(true)}
                    />
                  </div>
                  <div className="author-info">
                    <span className="author-name">Mensaje del Ejecutivo</span>
                    <span className="author-party">{`${president.name} · ${president.term_label}`}</span>
                  </div>
                </div>
              ) : (
                <div className="author-item">
                  <div className="author-avatar mensaje-avatar">🏛</div>
                  <div className="author-info">
                    <span className="author-name">Mensaje del Ejecutivo</span>
                    <span className="author-party">
                      {president != null
                        ? `${president.name} · ${president.term_label}`
                        : 'Presidencia de la República'}
                    </span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  )
}
